import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { PreferencesProvider } from "@/lib/preferences";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireOnboarded } from "@/components/RequireOnboarded";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Admin from "@/pages/Admin";
import Onboarding from "@/pages/Onboarding";
import Assessment from "@/pages/Assessment";
import AssessmentLaneChoice from "@/pages/AssessmentLaneChoice";
import AssessmentReview from "@/pages/AssessmentReview";
import AssessmentConsent from "@/pages/AssessmentConsent";
import AssessmentSubmitted from "@/pages/AssessmentSubmitted";
import QuickSnapshot from "@/pages/QuickSnapshot";
import Results from "@/pages/Results";
import Learning from "@/pages/Learning";
import ModulePage from "@/pages/ModulePage";
import CapstonePage from "@/pages/CapstonePage";
import PathwayPage from "@/pages/PathwayPage";
import Portfolio from "@/pages/Portfolio";
import ActionPlan from "@/pages/ActionPlan";
import Opportunity from "@/pages/Opportunity";
import Briefing from "@/pages/Briefing";
import AdminM5 from "@/pages/AdminM5";
import AdminContent from "@/pages/AdminContent";
import AdminProducts from "@/pages/AdminProducts";
import Plans from "@/pages/Plans";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import RefundPolicy from "@/pages/RefundPolicy";
import Training from "@/pages/Training";
import TrainingLesson from "@/pages/TrainingLesson";
import EntryAssessment from "@/pages/EntryAssessment";
import TrainingPracticeDay from "@/pages/TrainingPracticeDay";
import TrainingKnowledgeCheck from "@/pages/TrainingKnowledgeCheck";
import ResumeStudioList from "@/pages/ResumeStudioList";
import ResumeStudioNew from "@/pages/ResumeStudioNew";
import ResumeStudioEditor from "@/pages/ResumeStudioEditor";
import ResumeStudioUpload from "@/pages/ResumeStudioUpload";
import ResumeStudioPreview from "@/pages/ResumeStudioPreview";
import ResumeStudioVersions from "@/pages/ResumeStudioVersions";
import { RequireResumeStudioConsent } from "@/components/RequireResumeStudioConsent";

function Root() {
  const { loading } = useAuth();
  if (loading) return <Landing />;
  // Landing serves all three states (signed-out, signed-in unpaid,
  // signed-in paid) per the FIX 2 hero-CTA spec. Signed-in users can
  // still get to their dashboard via the app-shell navigation or the
  // landing CTAs themselves.
  return <Landing />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <PreferencesProvider enabled={!!user}>
      <Toaster position="top-right" theme="dark" richColors closeButton />
      <Routes>
        <Route path="/" element={<Root />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route
          path="/onboarding"
          element={<RequireAuth><Onboarding /></RequireAuth>}
        />
        <Route
          path="/dashboard"
          element={<RequireOnboarded><Dashboard /></RequireOnboarded>}
        />
        <Route
          path="/settings"
          element={<RequireOnboarded><Settings /></RequireOnboarded>}
        />
        <Route
          path="/assessment/choose"
          element={<RequireOnboarded><AssessmentLaneChoice /></RequireOnboarded>}
        />
        <Route
          path="/assessment"
          element={<RequireOnboarded><Assessment /></RequireOnboarded>}
        />
        <Route
          path="/assessment/review"
          element={<RequireOnboarded><AssessmentReview /></RequireOnboarded>}
        />
        <Route
          path="/assessment/consent"
          element={<RequireOnboarded><AssessmentConsent /></RequireOnboarded>}
        />
        <Route
          path="/assessment/submitted"
          element={<RequireOnboarded><AssessmentSubmitted /></RequireOnboarded>}
        />
        <Route
          path="/assessment/quick/snapshot"
          element={<RequireOnboarded><QuickSnapshot /></RequireOnboarded>}
        />
        {/* URL alias for discoverability — kebab-case matches historical
             external links/emails and avoids the /quick/snapshot slash confusion. */}
        <Route
          path="/assessment/quick-snapshot"
          element={<RequireOnboarded><QuickSnapshot /></RequireOnboarded>}
        />
        <Route
          path="/results"
          element={<RequireOnboarded><Results /></RequireOnboarded>}
        />
        <Route
          path="/briefing"
          element={<RequireOnboarded><Briefing /></RequireOnboarded>}
        />
        <Route
          path="/learning"
          element={<RequireOnboarded><Learning /></RequireOnboarded>}
        />
        <Route
          path="/learning/module/:moduleId"
          element={<RequireOnboarded><ModulePage /></RequireOnboarded>}
        />
        <Route
          path="/learning/pathway/:pathwayId"
          element={<RequireOnboarded><PathwayPage /></RequireOnboarded>}
        />
        <Route
          path="/learning/project/:projectId"
          element={<RequireOnboarded><CapstonePage /></RequireOnboarded>}
        />
        <Route
          path="/portfolio"
          element={<RequireOnboarded><Portfolio /></RequireOnboarded>}
        />
        <Route
          path="/action-plan"
          element={<RequireOnboarded><ActionPlan /></RequireOnboarded>}
        />
        <Route
          path="/opportunity"
          element={<RequireOnboarded><Opportunity /></RequireOnboarded>}
        />
        <Route
          path="/resume-studio"
          element={<RequireOnboarded><RequireResumeStudioConsent><ResumeStudioList /></RequireResumeStudioConsent></RequireOnboarded>}
        />
        <Route
          path="/resume-studio/new"
          element={<RequireOnboarded><RequireResumeStudioConsent><ResumeStudioNew /></RequireResumeStudioConsent></RequireOnboarded>}
        />
        <Route
          path="/resume-studio/:resumeId"
          element={<RequireOnboarded><RequireResumeStudioConsent><ResumeStudioEditor /></RequireResumeStudioConsent></RequireOnboarded>}
        />
        <Route
          path="/resume-studio/:resumeId/upload"
          element={<RequireOnboarded><RequireResumeStudioConsent><ResumeStudioUpload /></RequireResumeStudioConsent></RequireOnboarded>}
        />
        <Route
          path="/resume-studio/:resumeId/preview"
          element={<RequireOnboarded><RequireResumeStudioConsent><ResumeStudioPreview /></RequireResumeStudioConsent></RequireOnboarded>}
        />
        <Route
          path="/resume-studio/:resumeId/versions"
          element={<RequireOnboarded><RequireResumeStudioConsent><ResumeStudioVersions /></RequireResumeStudioConsent></RequireOnboarded>}
        />
        <Route
          path="/admin/m5"
          element={<RequireAuth adminOnly><AdminM5 /></RequireAuth>}
        />
        <Route
          path="/admin/content"
          element={<RequireAuth adminOnly><AdminContent /></RequireAuth>}
        />
        <Route
          path="/admin/products"
          element={<RequireAuth adminOnly><AdminProducts /></RequireAuth>}
        />
        <Route
          path="/plans"
          element={<Plans />}
        />
        <Route
          path="/training"
          element={<RequireOnboarded><Training /></RequireOnboarded>}
        />
        <Route
          path="/training/assessment"
          element={<RequireOnboarded><EntryAssessment /></RequireOnboarded>}
        />
        <Route
          path="/training/practice/:dayId"
          element={<RequireOnboarded><TrainingPracticeDay /></RequireOnboarded>}
        />
        <Route
          path="/training/month-knowledge-check/:section"
          element={<RequireOnboarded><TrainingKnowledgeCheck /></RequireOnboarded>}
        />
        <Route
          path="/training/lesson/:lessonId"
          element={<RequireOnboarded><TrainingLesson /></RequireOnboarded>}
        />
        <Route
          path="/payment/success"
          element={<PaymentSuccess />}
        />
        <Route
          path="/payment/cancel"
          element={<RequireAuth><PaymentCancel /></RequireAuth>}
        />
        <Route
          path="/admin"
          element={<RequireAuth adminOnly><Admin /></RequireAuth>}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PreferencesProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
